import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import CancelModalWindow from '../modals/CancelModalWindow.tsx';
import DataTable from '@containers/DataTable';
import NoItems from '@components/NoItems';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import TrackingNumber from '@components/TrackingNumber';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonSize } from '@components/Button';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { formatDate, formatSumFromObject, formatNumber } from '@utils/formatValues';
import { getReturnsPage, Return, ReturnTypes } from '@api/apiClient';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import { useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

const DisposalTab = ({
  filters,
  pending,
  downloadingBtn,
  handlerSetSavedDisposalSelection,
  savedDisposalSelection,
  refreshData,
  sellersNotFound,
  noData,
}: {
  filters: FilterModel[],
  pending: boolean,
  downloadingBtn: ReactElement,
  handlerSetSavedDisposalSelection: (value: (((prevState: string[]) => string[]) | string[])) => void,
  savedDisposalSelection: string[],
  refreshData: () => void,
  sellersNotFound: boolean,
  noData: boolean,
}) => {
  const [cancelId, setIsCancelId] = useState('');
  const [detailId, setDetailId] = useState<string>('');
  const [isOpenCancelWindow, setIsOpenCancelWindow] = useState(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);

  const { t } = useTranslation();

  const columns: TableColumn<Return>[] = [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<Return>) => (
          <TrackingNumber
              externalId={row.data?.externalId}
              onClick={() => {
                setDetailId(row.data?.id);
                setIsOpenParcelWindow(true);
              }}
              trackNumber={row.data?.trackNumber}
          />
      ),
      fixWidth: true,
      noOverflow: true,
      width: "200px"
    },
    {
      header: t("disposalTable.service"),
      accessor: (row: TableRowType<Return>) => row.data.serviceName,
      alignment: "left",
      width: "200px"
    },
    {
      header: t("disposalTable.attachment"),
      accessor: (row: TableRowType<Return>) => {
        const firstItem = row.data.items?.[0];
        return firstItem ? (
          <a href={firstItem.url ?? ""} target="_blank" rel="noreferrer">
            {firstItem.name ?? ""}
          </a>
        ) : null;
      },
      width: "235px"
    },
    {
      header: t("disposalTable.cost"),
      accessor: (row: TableRowType<Return>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {formatSumFromObject(row.data?.declaredValue)}
          </Text>
      ),
      alignment: "right",
      width: "120px"
    },
    {
      header: t('disposalTable.weight'),
      accessor: (row: TableRowType<Return>) => (
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {row.data.weight ? formatNumber(row.data.weight.value) : '—'}
          </Text>
      ),
    },
    {
      header: t("disposalTable.seller"),
      accessor: (row: TableRowType<Return>) => (
        <AvatarWithMarketplace
          id={row.data.sellerId}
          name={row.data.sellerName}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t("disposalTable.returnAt"),
      accessor: (row: TableRowType<Return>) => formatDate(row.data.returnAt, "dd.MM.yy HH:mm"),
      type: TableColumnType.TERTIARY
    },
    {
      header: t("disposalTable.disposed"),
      accessor: (row: TableRowType<Return>) => (
        <>
          {row.data.isCancelable ? (
            <Button
              onClick={() => {
                setIsCancelId(row.data.id);
                setIsOpenCancelWindow(true);
              }}
              size={ButtonSize.XS}
              isRounded
              title={t("disposalTable.resetButton")}
            />
          ) : (
              <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
              >
                {(row.data.returnTypeChangeInitiator === 'SellerDashboard' || row.data.returnTypeChangeInitiator === 'WeChat') ? t('disposalTable.bySeller') : t('disposalTable.expired')}
              </Text>
          )}
        </>
      ),
      alignment: 'center',
      noOverflow: true
    },
  ];

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  };

  if (sellersNotFound) {
    return (
      <AddSellerPanel isFirstSeller />
    );
  }

  if (noData) {
    return (
      <NoItems
        title={t("noItems.returns.title")}
        description={t("noItems.returns.description")}
      />
    )
  }

  return (
    <TableSelectionPanel
        isShown={savedDisposalSelection.length > 0}
        items={[
          {
            label: t("awaitingTableSelectionPanel.selected"),
            value: savedDisposalSelection.length,
          }
        ]}
        buttons={[downloadingBtn]}
    >
      <DataTable<Return>
        queryKey="returns-page"
        pending={pending}
        getData={getReturnsPage}
        columns={columns}
        selectable
        selectedIds={savedDisposalSelection}
        onSelectionChange={handlerSetSavedDisposalSelection}
        notFound={t("disposalTab.notDisposal")}
        filters={[
          ...filters,
          { name: "ReturnTypes", value: [ReturnTypes.Disposal] },
        ]}
      />

      <Modal
        isOpen={isOpenCancelWindow}
        onClose={() => {
          setIsOpenCancelWindow(false);
        }}
        onClickOverlay={() => {
          setIsOpenCancelWindow(false);
        }}
      >
        <CancelModalWindow
          parcelId={cancelId}
          onSuccess={() => {
            handlerSetSavedDisposalSelection([]);
            refreshData()
            setIsCancelId("");
            setIsOpenCancelWindow(false);
          }}
          onClose={() => {
            setIsOpenCancelWindow(false);
            setIsCancelId("");
          }}
        />
      </Modal>
      <Modal
          bodyWithoutIndentations
          isOpen={isOpenParcelWindow}
          onClickOverlay={closeParcelWindow}
          onClose={closeParcelWindow}
          width={900}
      >
        <ParcelModalWindow parcelId={detailId}/>
      </Modal>
    </TableSelectionPanel>
  );
};

export default DisposalTab;
